import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Edit3,
  Trash2,
  FileText,
  Video,
  Image,
  Link as LinkIcon,
  ArrowLeft,
  X,
  Download,
  Folder,
  FolderPlus,
  Upload,
  MoreVertical,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "react-hot-toast";
import TiptapEditor from "../../components/common/TiptapEditor";
import {
  courseMaterialService,
  CourseMaterial,
  CourseModule,
} from "../../services/courseMaterialService";

const CourseMaterialManagement: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateModuleModal, setShowCreateModuleModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<CourseMaterial | null>(
    null,
  );
  const [editingModule, setEditingModule] = useState<CourseModule | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [bulkUploadMode, setBulkUploadMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submittingModule, setSubmittingModule] = useState(false);
  const [openMaterialMenuId, setOpenMaterialMenuId] = useState<string | null>(
    null,
  );
  const materialMenuRef = useRef<HTMLDivElement>(null);
  const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        materialMenuRef.current &&
        !materialMenuRef.current.contains(event.target as Node)
      ) {
        setOpenMaterialMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [formData, setFormData] = useState<Omit<CourseMaterial, "_id">>({
    title: "",
    description: "",
    type: "document",
    fileUrl: "",
    content: "",
  });

  const [moduleFormData, setModuleFormData] = useState<
    Omit<CourseModule, "_id" | "materials">
  >({
    name: "",
    description: "",
    order: 0,
  });

  useEffect(() => {
    if (courseId) {
      loadModules();
    }
  }, [courseId]);

  const loadModules = async () => {
    if (!courseId) return;

    try {
      setLoading(true);
      const data = await courseMaterialService.getCourseMaterials(courseId);
      setModules(data);
      setExpandedModuleId((current) =>
        current && data.some((m) => m._id === current)
          ? current
          : data[0]?._id || null,
      );
    } catch (error) {
      console.error("Error loading modules:", error);
      toast.error("Failed to load course materials");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMaterial = (moduleId?: string) => {
    setFormData({
      title: "",
      description: "",
      type: "document",
      fileUrl: "",
      content: "",
    });
    setSelectedFile(null);
    setSelectedFiles([]);
    setEditingMaterial(null);
    setSelectedModuleId(moduleId || null);
    setShowCreateModal(true);
  };

  const handleEditMaterial = (material: CourseMaterial, moduleId: string) => {
    setFormData({
      title: material.title,
      description: material.description || "",
      type: material.type,
      fileUrl: material.fileUrl || "",
      content: material.content || "",
    });
    setSelectedFile(null);
    setEditingMaterial(material);
    setSelectedModuleId(moduleId);
    setShowCreateModal(true);
  };

  const handleSubmitMaterial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId || !selectedModuleId) return;

    const selectedModule = modules.find((m) => m._id === selectedModuleId);
    if (!selectedModule) return;

    if (
      !bulkUploadMode &&
      formData.type === "text" &&
      !formData.content?.trim()
    ) {
      toast.error("Content is required for text materials");
      return;
    }

    setUploading(true);
    try {
      if (bulkUploadMode) {
        // Bulk upload
        const result = await courseMaterialService.bulkUploadMaterials(
          courseId,
          selectedModuleId,
          selectedModule.name,
          selectedFiles,
        );

        if (result.success) {
          toast.success(
            `Successfully uploaded ${result.uploaded} material${
              result.uploaded !== 1 ? "s" : ""
            }`,
          );
        } else {
          toast.success(
            `Uploaded ${result.uploaded} material${
              result.uploaded !== 1 ? "s" : ""
            }, ${result.failed} failed`,
          );
          if (result.errors.length > 0) {
            console.error("Bulk upload errors:", result.errors);
          }
        }
      } else if (editingMaterial) {
        // Update existing material
        await courseMaterialService.updateModuleMaterial(
          courseId,
          selectedModuleId,
          selectedModule.name,
          editingMaterial._id!,
          formData,
          selectedFile || undefined,
        );
        toast.success("Material updated successfully");
      } else {
        // Single material upload
        await courseMaterialService.addModuleMaterial(
          courseId,
          selectedModuleId,
          selectedModule.name,
          formData,
          selectedFile || undefined,
        );
        toast.success("Material added successfully");
      }

      setShowCreateModal(false);
      setSelectedFile(null);
      setSelectedFiles([]);
      setBulkUploadMode(false);
      loadModules();
    } catch (error: any) {
      console.error("Error saving material:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save material";
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMaterial = async (
    material: CourseMaterial,
    moduleId: string,
  ) => {
    if (!courseId || !material._id) return;

    if (window.confirm("Are you sure you want to delete this material?")) {
      try {
        await courseMaterialService.deleteModuleMaterial(
          courseId,
          moduleId,
          material._id,
        );
        toast.success("Material deleted successfully");
        loadModules();
      } catch (error) {
        console.error("Error deleting material:", error);
        toast.error("Failed to delete material");
      }
    }
  };

  const toggleModuleExpanded = (moduleId: string) => {
    setExpandedModuleId((current) => (current === moduleId ? null : moduleId));
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="w-5 h-5 text-red-500" />;
      case "image":
        return <Image className="w-5 h-5 text-green-500" />;
      case "link":
        return <LinkIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const handleCreateModule = () => {
    setModuleFormData({
      name: "",
      description: "",
      order: modules.length,
    });
    setEditingModule(null);
    setShowCreateModuleModal(true);
  };

  const handleEditModule = (module: CourseModule) => {
    setModuleFormData({
      name: module.name,
      description: module.description || "",
      order: module.order || 0,
    });
    setEditingModule(module);
    setShowCreateModuleModal(true);
  };

  const handleSubmitModule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId) return;

    setSubmittingModule(true);
    try {
      if (editingModule) {
        await courseMaterialService.updateModule(
          courseId,
          editingModule._id!,
          moduleFormData,
        );
        toast.success("Module updated successfully");
      } else {
        await courseMaterialService.createModule(courseId, moduleFormData);
        toast.success("Module created successfully");
      }
      setShowCreateModuleModal(false);
      loadModules();
    } catch (error) {
      console.error("Error saving module:", error);
      toast.error("Failed to save module");
    } finally {
      setSubmittingModule(false);
    }
  };

  const handleDeleteModule = async (module: CourseModule) => {
    if (!courseId || !module._id) return;

    if (
      window.confirm(
        "Are you sure you want to delete this module and all its materials?",
      )
    ) {
      try {
        await courseMaterialService.deleteModule(courseId, module._id);
        toast.success("Module deleted successfully");
        loadModules();
      } catch (error) {
        console.error("Error deleting module:", error);
        toast.error("Failed to delete module");
      }
    }
  };

  const filteredModules = modules.filter((module) => {
    const moduleMatches =
      module.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (module.description &&
        module.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const materialMatches = module.materials.some(
      (material) =>
        material.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (material.description &&
          material.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase())),
    );

    return moduleMatches || materialMatches;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-semibold transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Course
            </button>
            <div className="h-6 w-px bg-slate-300"></div>
            <h1 className="text-2xl font-bold text-slate-900">
              Course Materials
            </h1>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleCreateModule}
              className="flex items-center gap-2 bg-gradient-to-br from-emerald-300 to-emerald-400 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <FolderPlus size={18} />
              Add Module
            </button>
            <button
              onClick={() => {
                setBulkUploadMode(true);
                handleCreateMaterial();
              }}
              className="flex items-center gap-2 bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <Upload size={18} />
              Bulk Upload
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search materials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
          />
        </div>
      </div>

      {/* Modules and Materials */}
      {filteredModules.length === 0 ? (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12 text-center">
          <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
            <Folder className="text-white" size={32} />
          </div>
          <h3 className="mt-2 text-lg font-bold text-slate-900">
            No modules or materials yet
          </h3>
          <p className="mt-1 text-slate-600 font-medium">
            Start by creating modules to organize your course materials.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredModules.map((module) => (
            <div
              key={module._id}
              className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-y-auto hover:shadow-2xl transition-all duration-300"
            >
              {/* Module Header */}
              <button
                onClick={() => toggleModuleExpanded(module._id!)}
                className="w-full flex items-center gap-4 bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-5 border-b border-slate-200 text-left"
              >
                <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-3 shadow-lg flex-shrink-0">
                  <Folder className="text-white" size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-900 truncate">
                    {module.name}
                  </h3>
                  {module.description && (
                    <p className="text-sm text-slate-600 font-medium truncate">
                      {module.description}
                    </p>
                  )}
                </div>
                <span className="text-slate-500 flex-shrink-0">
                  {expandedModuleId === module._id ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </span>
              </button>

              {/* Module Materials */}
              {expandedModuleId === module._id && (
                <>
                  <div className="flex items-center justify-end gap-2 px-6 py-4 border-b border-slate-200">
                    <button
                      onClick={() => handleCreateMaterial(module._id)}
                      className="flex items-center gap-2 bg-gradient-to-br from-blue-300 to-indigo-400 text-white px-4 py-2 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      <Plus size={16} />
                      Material
                    </button>
                    <button
                      onClick={() => handleEditModule(module)}
                      className="bg-gradient-to-br from-amber-300 to-yellow-400 rounded-lg p-2 text-white hover:scale-110 transition-all duration-300 shadow-sm"
                      title="Edit Module"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteModule(module)}
                      className="bg-gradient-to-br from-red-500 to-red-500 rounded-lg p-2 text-white hover:scale-110 transition-all duration-300 shadow-sm"
                      title="Delete Module"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {module.materials.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-xl p-3 w-12 h-12 mx-auto shadow-lg flex items-center justify-center mb-3">
                        <FileText className="text-white" size={24} />
                      </div>
                      <p className="text-sm text-slate-600 font-medium">
                        No materials in this module yet.
                      </p>
                    </div>
                  ) : (
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {module.materials.map((material) => (
                          <div
                            key={material._id}
                            className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200 p-4 hover:shadow-lg hover:scale-105 transition-all duration-300"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div
                                  className={`rounded-lg p-2 shadow-sm flex-shrink-0 ${
                                    material.type === "video"
                                      ? "bg-gradient-to-br from-red-300 to-pink-400"
                                      : material.type === "image"
                                        ? "bg-gradient-to-br from-emerald-300 to-emerald-400"
                                        : material.type === "link"
                                          ? "bg-gradient-to-br from-blue-300 to-indigo-400"
                                          : "bg-gradient-to-br from-purple-300 to-purple-400"
                                  }`}
                                >
                                  {material.type === "video" ? (
                                    <Video className="text-white" size={20} />
                                  ) : material.type === "image" ? (
                                    <Image className="text-white" size={20} />
                                  ) : material.type === "link" ? (
                                    <LinkIcon
                                      className="text-white"
                                      size={20}
                                    />
                                  ) : (
                                    <FileText
                                      className="text-white"
                                      size={20}
                                    />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-bold text-slate-900 text-sm truncate">
                                    {material.title}
                                  </h4>
                                  <p className="text-xs text-slate-600 font-medium capitalize">
                                    {material.type}
                                  </p>
                                </div>
                              </div>

                              <div
                                className="relative flex-shrink-0"
                                ref={
                                  openMaterialMenuId === material._id
                                    ? materialMenuRef
                                    : undefined
                                }
                              >
                                <button
                                  onClick={() =>
                                    setOpenMaterialMenuId(
                                      openMaterialMenuId === material._id
                                        ? null
                                        : material._id!,
                                    )
                                  }
                                  className="p-1.5 rounded-lg text-black hover:bg-slate-100 transition-colors duration-200"
                                  title="Options"
                                  style={{
                                    color: "black",
                                    backgroundColor: "transparent",
                                  }}
                                >
                                  <MoreVertical size={16} />
                                </button>

                                {openMaterialMenuId === material._id && (
                                  <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden">
                                    {material.fileUrl && (
                                      <a
                                        href={material.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() =>
                                          setOpenMaterialMenuId(null)
                                        }
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-black hover:bg-slate-100 transition-colors"
                                      >
                                        <Download size={14} />
                                        Download
                                      </a>
                                    )}
                                    <button
                                      onClick={() => {
                                        handleEditMaterial(
                                          material,
                                          module._id!,
                                        );
                                        setOpenMaterialMenuId(null);
                                      }}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm font-medium text-black !hover:bg-slate-100 transition-colors"
                                      style={{
                                        color: "black",
                                        backgroundColor: "transparent",
                                      }}
                                    >
                                      <Edit3 size={14} />
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleDeleteMaterial(
                                          material,
                                          module._id!,
                                        );
                                        setOpenMaterialMenuId(null);
                                      }}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm font-medium text-black hover:bg-slate-100 transition-colors"
                                      style={{
                                        color: "black",
                                        backgroundColor: "transparent",
                                      }}
                                    >
                                      <Trash2 size={14} />
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>

                            {material.description && (
                              <p className="text-slate-600 text-xs font-medium line-clamp-2 mt-2">
                                {material.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                {editingMaterial
                  ? "Edit Material"
                  : bulkUploadMode
                    ? "Bulk Upload Materials"
                    : "Add New Material"}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setBulkUploadMode(false);
                  setSelectedFiles([]);
                }}
                className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-lg p-2 text-white hover:scale-110 transition-all duration-300 shadow-sm"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitMaterial} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Module *
                </label>
                <select
                  required
                  value={selectedModuleId || ""}
                  onChange={(e) => setSelectedModuleId(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                >
                  <option value="">Select a module</option>
                  {modules.map((module) => (
                    <option key={module._id} value={module._id}>
                      {module.name}
                    </option>
                  ))}
                </select>
              </div>

              {!bulkUploadMode && (
                <>
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="Enter material title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      placeholder="Enter material description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Type *
                    </label>
                    <select
                      required
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          type: e.target.value as any,
                        })
                      }
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    >
                      <option value="document">Document</option>
                      <option value="video">Video</option>
                      <option value="image">Image</option>
                      <option value="link">External Link</option>
                      <option value="text">Text Content</option>
                    </select>
                  </div>
                </>
              )}

              {bulkUploadMode && (
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Select Multiple Files *
                  </label>
                  <input
                    type="file"
                    multiple
                    required
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setSelectedFiles(files);
                    }}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.mp4,.avi,.mov,.wmv,.jpg,.jpeg,.png,.gif,.bmp"
                  />
                  {selectedFiles.length > 0 && (
                    <div className="mt-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-3 border border-slate-200">
                      <p className="text-sm font-bold text-blue-600 mb-1">
                        Selected {selectedFiles.length} file(s):
                      </p>
                      <ul className="text-xs text-slate-600 font-medium max-h-20 overflow-y-auto space-y-1">
                        {selectedFiles.map((file, index) => (
                          <li key={index}>• {file.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {formData.type === "link" && (
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    URL *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.fileUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, fileUrl: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    placeholder="https://example.com"
                  />
                </div>
              )}

              {formData.type === "text" && (
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Content *
                  </label>
                  <div className="material-content-editor rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                    <TiptapEditor
                      value={formData.content}
                      onChange={(value) =>
                        setFormData({ ...formData, content: value })
                      }
                      placeholder="Enter text content"
                    />
                  </div>
                </div>
              )}

              {!bulkUploadMode &&
                ["document", "video", "image"].includes(formData.type) && (
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      File Upload
                    </label>
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setSelectedFile(file);
                          // Update title if it's empty
                          if (!formData.title) {
                            setFormData({ ...formData, title: file.name });
                          }
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      accept={
                        formData.type === "image"
                          ? "image/*"
                          : formData.type === "video"
                            ? "video/*"
                            : ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                      }
                    />
                    {selectedFile && (
                      <p className="mt-2 text-sm font-bold text-blue-600">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                    {formData.fileUrl && !selectedFile && (
                      <p className="mt-2 text-sm font-bold text-emerald-600">
                        File uploaded successfully
                      </p>
                    )}
                  </div>
                )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setBulkUploadMode(false);
                    setSelectedFiles([]);
                  }}
                  className="px-5 py-2.5 border-2 border-slate-200 text-slate-700 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    uploading || (bulkUploadMode && selectedFiles.length === 0)
                  }
                  className="px-5 py-2.5 bg-gradient-to-br from-blue-300 to-indigo-400 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {uploading
                    ? bulkUploadMode
                      ? "Uploading..."
                      : "Uploading..."
                    : editingMaterial
                      ? "Update"
                      : bulkUploadMode
                        ? `Upload ${selectedFiles.length} Material${
                            selectedFiles.length !== 1 ? "s" : ""
                          }`
                        : "Create"}{" "}
                  Material
                  {bulkUploadMode && selectedFiles.length !== 1 ? "s" : ""}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Module Modal */}
      {showCreateModuleModal && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                {editingModule ? "Edit Module" : "Create New Module"}
              </h2>
              <button
                onClick={() => setShowCreateModuleModal(false)}
                className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-lg p-2 text-white hover:scale-110 transition-all duration-300 shadow-sm"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitModule} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Module Name *
                </label>
                <input
                  type="text"
                  required
                  value={moduleFormData.name}
                  onChange={(e) =>
                    setModuleFormData({
                      ...moduleFormData,
                      name: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder="Enter module name"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Description
                </label>
                <textarea
                  value={moduleFormData.description}
                  onChange={(e) =>
                    setModuleFormData({
                      ...moduleFormData,
                      description: e.target.value,
                    })
                  }
                  rows={3}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder="Enter module description"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Order
                </label>
                <input
                  type="number"
                  value={moduleFormData.order || ""}
                  onChange={(e) =>
                    setModuleFormData({
                      ...moduleFormData,
                      order:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  placeholder="Enter display order"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModuleModal(false)}
                  className="px-5 py-2.5 border-2 border-slate-200 text-slate-700 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingModule}
                  className="px-5 py-2.5 bg-gradient-to-br from-emerald-300 to-emerald-400 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {submittingModule
                    ? "Saving..."
                    : (editingModule ? "Update" : "Create") + " Module"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseMaterialManagement;
